<template>
    <div class="duration-suggest" :class="{ 'is-invalid': invalid, open: suggestionsOpen }">
        <div class="duration-input-wrap">
            <input
                ref="input"
                :id="inputId"
                :value="draft"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :disabled="disabled"
                :placeholder="placeholder"
                :aria-expanded="suggestionsOpen ? 'true' : 'false'"
                aria-autocomplete="list"
                :aria-controls="`${inputId}-suggestions`"
                @focus="handleFocus"
                @input="handleInput"
                @blur="handleBlur"
                @keydown.down.prevent="moveSelection(1)"
                @keydown.up.prevent="moveSelection(-1)"
                @keydown.enter.prevent="selectActiveSuggestion"
                @keydown.esc.prevent="closeSuggestions"
            />
            <i class="fa-regular fa-clock"></i>
        </div>

        <ul
            v-if="suggestionsOpen"
            :id="`${inputId}-suggestions`"
            class="duration-suggestions"
            role="listbox"
        >
            <li v-for="(suggestion, index) in suggestions" :key="suggestion.unit" role="option">
                <button
                    type="button"
                    :class="{ active: index === activeIndex }"
                    :aria-selected="index === activeIndex ? 'true' : 'false'"
                    @mouseenter="activeIndex = index"
                    @mousedown.prevent="selectSuggestion(suggestion)"
                >
                    <i :class="suggestion.unit === 'minutes' ? 'fa-solid fa-stopwatch' : 'fa-solid fa-clock'"></i>
                    {{ suggestion.label }}
                </button>
            </li>
            <li v-if="suggestions.length === 0" class="minimum-hint">
                {{ minimumHint }}
            </li>
        </ul>
    </div>
</template>

<script>
    export default {
        name: 'DurationSuggestInput',
        props: {
            value: {
                type: [Number, String],
                default: null
            },
            minMinutes: {
                type: [Number, String],
                default: 1
            },
            preferredUnit: {
                type: String,
                default: 'minutes',
                validator: (value) => ['minutes', 'hours'].includes(value)
            },
            inputId: {
                type: String,
                required: true
            },
            placeholder: {
                type: String,
                default: ''
            },
            invalid: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                draft: '',
                focused: false,
                activeIndex: 0,
                selectedUnit: this.preferredUnit
            }
        },
        computed: {
            normalizedValue() {
                if (this.value === null || this.value === '') return null
                const value = Number(this.value)
                return Number.isFinite(value) ? value : null
            },
            normalizedMinMinutes() {
                const value = Number(this.minMinutes)
                return Number.isFinite(value) && value >= 0 ? value : 0
            },
            typedAmount() {
                const value = Number(String(this.draft).trim().replace(',', '.'))
                return Number.isFinite(value) && value > 0 ? value : null
            },
            suggestions() {
                if (this.typedAmount === null) return []

                return [
                    this.createSuggestion('minutes', this.typedAmount),
                    this.createSuggestion('hours', this.typedAmount)
                ].filter((suggestion) => suggestion.minutes >= this.normalizedMinMinutes)
            },
            suggestionsOpen() {
                return this.focused && this.typedAmount !== null
            },
            minimumHint() {
                return this.__('notification_management.minimum_duration_hint').replace(
                    ':duration',
                    this.formatDuration(this.normalizedMinMinutes, 'minutes')
                )
            }
        },
        watch: {
            value: {
                immediate: true,
                handler() {
                    if (!this.focused) this.syncDraftFromValue()
                }
            },
            preferredUnit(value) {
                this.selectedUnit = value
                if (!this.focused) this.syncDraftFromValue()
            }
        },
        methods: {
            createSuggestion(unit, amount) {
                return {
                    unit,
                    minutes: unit === 'hours' ? amount * 60 : amount,
                    label: this.formatDuration(amount, unit)
                }
            },
            formatDuration(amount, unit) {
                const locale = this.$locale() || 'it'
                const formattedAmount = new Intl.NumberFormat(locale, {
                    maximumFractionDigits: 4
                }).format(amount)
                const singular = Number(amount) === 1
                const unitKey = singular
                    ? unit === 'hours'
                        ? 'hour_unit'
                        : 'minute_unit'
                    : unit === 'hours'
                        ? 'hours_unit'
                        : 'minutes_unit'

                return `${formattedAmount} ${this.__(`notification_management.${unitKey}`)}`
            },
            amountFromValue(unit = this.selectedUnit) {
                if (this.normalizedValue === null) return null
                return unit === 'hours' ? this.normalizedValue / 60 : this.normalizedValue
            },
            syncDraftFromValue() {
                const amount = this.amountFromValue()
                this.draft = amount === null ? '' : this.formatDuration(amount, this.selectedUnit)
            },
            handleFocus() {
                this.focused = true
                const amount = this.amountFromValue()
                this.draft = amount === null ? '' : String(amount)
                this.activeIndex = 0
                this.$nextTick(() => this.$refs.input?.select())
            },
            handleInput(event) {
                this.draft = event.target.value
                this.activeIndex = 0

                if (!this.draft.trim()) this.$emit('input', null)
            },
            handleBlur() {
                this.focused = false
                this.syncDraftFromValue()
            },
            closeSuggestions() {
                this.focused = false
                this.$refs.input?.blur()
            },
            moveSelection(direction) {
                if (this.suggestions.length === 0) return
                this.activeIndex =
                    (this.activeIndex + direction + this.suggestions.length) % this.suggestions.length
            },
            selectActiveSuggestion() {
                const suggestion = this.suggestions[this.activeIndex]
                if (suggestion) this.selectSuggestion(suggestion)
            },
            selectSuggestion(suggestion) {
                this.selectedUnit = suggestion.unit
                this.draft = suggestion.label
                this.$emit('input', suggestion.minutes)
                this.focused = false
                this.$refs.input?.blur()
            }
        }
    }
</script>

<style scoped lang="scss">
    .duration-suggest {
        position: relative;
    }

    .duration-input-wrap {
        position: relative;
        display: flex;
        min-height: 42px;
        align-items: center;
        overflow: hidden;
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        background: #fff;

        input {
            width: 100%;
            min-width: 0;
            flex: 1;
            border: 0;
            padding: 8px 38px 8px 12px;
            background: transparent;
            color: #333;
            outline: 0;
            font-size: 14px;
        }

        >i {
            position: absolute;
            right: 13px;
            color: #7a8590;
            pointer-events: none;
        }
    }

    .duration-suggest.open .duration-input-wrap,
    .duration-input-wrap:focus-within {
        border-color: var(--dark-blue);
        box-shadow: 0 0 0 3px rgba(29, 95, 159, 0.12);
    }

    .duration-suggest.is-invalid .duration-input-wrap {
        border-color: var(--danger);
    }

    .duration-suggestions {
        position: absolute;
        z-index: 20;
        top: calc(100% + 5px);
        right: 0;
        left: 0;
        margin: 0;
        overflow: hidden;
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        padding: 5px;
        background: #fff;
        box-shadow: 0 10px 25px rgba(30, 45, 60, 0.16);
        list-style: none;

        button {
            display: flex;
            width: 100%;
            align-items: center;
            gap: 9px;
            border: 0;
            border-radius: 8px;
            padding: 9px 10px;
            background: transparent;
            color: #333;
            text-align: left;
            font-size: 12px;

            i {
                width: 16px;
                color: var(--dark-blue);
                text-align: center;
            }

            &:hover,
            &.active {
                background: rgba(29, 95, 159, 0.1);
            }
        }
    }

    .minimum-hint {
        padding: 9px 10px;
        color: #6b7785;
        font-size: 11px;
    }
</style>
